import { useEffect, useState } from 'react';

type ViewportSizeCategory = 'small' | 'medium' | 'large' | 'ultrawide';

interface ViewportInfo {
  width: number;
  screenWidth: number;
  category: ViewportSizeCategory;
  isUltrawide: boolean;
  isLargeDesktop: boolean;
  isWindowSmaller: boolean; // True if window is significantly smaller than screen
}

export function useScreenWidth(): ViewportInfo {
  const [viewportInfo, setViewportInfo] = useState<ViewportInfo>(() => {
    if (typeof window === 'undefined') {
      return { 
        width: 1920, 
        screenWidth: 1920, 
        category: 'large', 
        isUltrawide: false, 
        isLargeDesktop: true,
        isWindowSmaller: false
      };
    }
    
    const windowWidth = window.innerWidth;
    const screenWidth = window.screen.width;
    return {
      width: windowWidth,
      screenWidth,
      category: getCategoryFromWidth(windowWidth),
      isUltrawide: windowWidth >= 2200,
      isLargeDesktop: windowWidth >= 1600,
      isWindowSmaller: (screenWidth - windowWidth) > 400, // Window is 400px+ smaller than screen
    };
  });

  useEffect(() => {
    const updateViewportInfo = () => {
      const windowWidth = window.innerWidth;
      const screenWidth = window.screen.width;
      setViewportInfo({
        width: windowWidth,
        screenWidth,
        category: getCategoryFromWidth(windowWidth),
        isUltrawide: windowWidth >= 2200,
        isLargeDesktop: windowWidth >= 1600,
        isWindowSmaller: (screenWidth - windowWidth) > 400,
      });
    };

    // Listen for window resize
    window.addEventListener('resize', updateViewportInfo);

    return () => {
      window.removeEventListener('resize', updateViewportInfo);
    };
  }, []);

  return viewportInfo;
}

function getCategoryFromWidth(width: number): ViewportSizeCategory {
  if (width >= 2200) return 'ultrawide';
  if (width >= 1600) return 'large';
  if (width >= 1200) return 'medium';
  return 'small';
}